import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import EventBus from 'eventing-bus';
import { Nav, NavItem, NavLink, TabContent, TabPane, Button, Badge, Table, Modal, ModalBody, ModalHeader, Card, CardBody, Row, Col } from 'reactstrap';
import './Workspaces.css';
import { color } from '../../../../node_modules/@mui/system/index';

const NotifyHistory = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('history');
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (isOpen) {
      fetchNotificationHistory();
    }
  }, [isOpen, currentPage]);

  const fetchNotificationHistory = () => {
    setLoading(true);
    dispatch({
      type: 'GET_NOTIFICATION_HISTORY',
      payload: {
        page: currentPage,
        limit: 10,
        callback: (data, error) => {
          setLoading(false);
          if (error) {
            EventBus.publish('error', 'Failed to fetch notification history');
          } else if (data) {
            setNotifications(data.notifications || []);
            setTotalPages(data.pagination?.pages || 1);
          }
        }
      }
    });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  };

  const renderNotificationList = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading notification history...</p>
        </div>
      );
    }

    if (!notifications || notifications.length === 0) {
      return (
        <div className="text-center py-5 d-block">
          <i className="fa fa-inbox fa-3x text-muted mb-3 d-block"></i>
          <h5 className="text-muted d-block">No Notifications Yet</h5>
          <p className="text-muted small d-block">Your notification history will appear here</p>
        </div>
      );
    }

    return (
      <div className="notification-list notification-list-sidebar">
        {notifications.map((notification, index) => (
          <Card
            key={notification.id || index}
            className={`notification-card mb-3 ${selectedNotification?.id === notification.id ? 'selected' : ''}`}
            onClick={() => {
              setSelectedNotification(notification);
              setActiveTab('details');
            }}
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="notification-subject mb-0">{notification.subject}</h6>
                {notification.success ? (
                  <Badge color="success" pill>Sent</Badge>
                ) : (
                  <Badge color="danger" pill>Failed</Badge>
                )}
              </div>

              <p className="notification-preview text-muted small mb-2">
                {notification.message ?
                  (notification.message.length > 100 ?
                    notification.message.substring(0, 100) + '...' :
                    notification.message) :
                  'No message content'
                }
              </p>

              <div className="notification-meta">
                <Row>
                  <Col xs={6}>
                    <small className="text-muted">
                      <i className="fa fa-users mr-1"></i>
                      {notification.workspaceCount} workspaces
                    </small>
                  </Col>
                  <Col xs={6} className="text-right">
                    <small className="text-muted">
                      <i className="fa fa-envelope-o mr-1"></i>
                      {notification.uniqueEmails} emails
                    </small>
                  </Col>
                </Row>
                <small className="text-muted d-block mt-2">
                  <i className="fa fa-clock-o mr-1"></i>
                  {formatDate(notification.sentAt)}
                </small>
                <small className="text-muted d-block mt-1">
                  <i className="fa fa-user mr-1"></i>
                  Sent by: {notification.sentBy}
                </small>
              </div>
            </CardBody>
          </Card>
        ))}

        {totalPages > 1 && (
          <div className="pagination-controls text-center mt-4">
            <Button
              size="sm"
              color="primary"
              outline
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="mr-2"
            >
              <i className="fa fa-chevron-left"></i> Previous
            </Button>
            <span className="mx-3">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              size="sm"
              color="primary"
              outline
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="ml-2"
            >
              Next <i className="fa fa-chevron-right"></i>
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderNotificationDetails = () => {
    if (!selectedNotification) {
      return (
        <div className="text-center py-5 d-block">
          <i className="fa fa-hand-pointer-o fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">Select a Notification</h5>
          <p className="text-muted small">Click on a notification from the list to view details</p>
        </div>
      );
    }

    return (
      <div className="notification-details">
        <button
          size="sm"
          color="link"
          className="mb-3 pl-0"
          onClick={() => setActiveTab('history')}
          style={{ color: 'var(--primary-color)' }}
        >
          <i className="fa fa-arrow-left mr-2"></i>
          Back to List
        </button>

        <div className="detail-section mb-4">
          <h4>
            <i className="fa fa-envelope mr-2"></i>
            Notification Content
          </h4>
          <Card>
            <CardBody>
              <h6 className="font-weight-bold mb-3">Subject</h6>
              <p className="mb-4">{selectedNotification.subject}</p>

              <h6 className="font-weight-bold mb-3">Message</h6>
              <div className="message-content">
                {selectedNotification.message || 'No message content'}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="detail-section mb-4">
          <h4>
            <i className="fa fa-chart-bar mr-2"></i>
            Delivery Statistics
          </h4>
          <div className='content-dashboard'>
            {/* Statistics */}
            <div className='counter-sections'>
              <div className='counter-box style-two'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Total Members</h5>
                    <div className='CountUp-box'>
                      <h3 className="" style={{ color: '#2563EB' }}>
                        {selectedNotification.workspaceCount}
                      </h3>
                      <small className="text-muted">Total Workspaces</small>
                    </div>
                  </div>
                  <div className='right-icon'>
                    <span className="icon fa fa-building fa-2x" style={{ color: '#2563EB' }}></span>
                  </div>
                </div>
              </div>

              <div className='counter-box style-two'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Active Members</h5>
                    <div className='CountUp-box'>
                      <h3 className="text-success">{selectedNotification.uniqueEmails}</h3>
                      <small className="text-muted">Unique Recipients</small>
                    </div>
                  </div>
                  <div className='right-icon'>
                    <span className="icon fa fa-users fa-2x text-success"></span>
                  </div>
                </div>
              </div>

              <div className='counter-box style-two'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Paused Members</h5>
                    <div className='CountUp-box'>
                      <h3 className="" style={{ color: '#EF4D56' }}>{selectedNotification.sentCount}</h3>
                      <small className="text-muted">Successfully Sent</small>
                    </div>
                  </div>
                  <div className='right-icon'>
                    <i className="icon fa fa-check-circle fa-2x" style={{ color: '#EF4D56' }}></i>
                  </div>
                </div>
              </div>

              <div className='counter-box style-two'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Deactivated Members</h5>
                    <div className='CountUp-box'>
                      <h3 className="" style={{ color: '#EF4444' }}>{selectedNotification.failedCount}</h3>
                      <small className="text-muted">Failed</small>
                    </div>
                  </div>
                  <div className='right-icon'>
                    <i className="icon fa fa-times-circle fa-2x text-danger"></i>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {selectedNotification.recipientEmails && selectedNotification.recipientEmails.length > 0 && (
          <div className="detail-section mb-4">
            <h4>
              <i className="fa fa-at mr-2"></i>
              Email Recipients ({selectedNotification.recipientEmails.length})
            </h4>
            <Card>
              <CardBody>
                <div className="recipient-list">
                  {selectedNotification.recipientEmails.map((email, idx) => (
                    <div key={idx} className="recipient-item">
                      <i className="fa fa-envelope-o mr-2"></i>
                      <span>{email}</span>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
        )}

        <div className="detail-section">
          <h4>
            <i className="fa fa-info-circle mr-2"></i>
            Send Information
          </h4>
          <Card>
            <CardBody>
              <div className="info-row">
                <span className="info-label">Sent by:</span>
                <span className="info-value">{selectedNotification.sentBy}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Date & Time:</span>
                <span className="info-value">{formatDate(selectedNotification.sentAt)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className="info-value">
                  {selectedNotification.success ? (
                    <Badge color="success">Delivered Successfully</Badge>
                  ) : (
                    <Badge color="danger">Delivery Failed</Badge>
                  )}
                </span>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} modalClassName='right right-without-bg' backdrop={false} className={`main-modal right right-side-modal style-three`}>
        <ModalHeader toggle={onClose}>
          <h3> <i className="fa fa-history mr-2"></i> Notification History</h3>
        </ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>

            <div className='function-box workspace-area-detail mt-3'>
              <div className="sidebar-body notification-sidebar-area">
                <div className='tabs-area'>
                  {/* Navigation Tabs */}
                  <div className='tab-header'>
                    {/* Navigation Tabs */}
                    <Nav tabs className="workspace-tabs">
                      <NavItem>
                        <NavLink
                          className={activeTab === 'history' ? 'active' : ''}
                          onClick={() => setActiveTab('history')}
                        >
                          <i className="fa fa-list mr-2"></i>
                          History
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === 'details' ? 'active' : ''}
                          onClick={() => setActiveTab('details')}
                          disabled={!selectedNotification}
                        >
                          <i className="fa fa-info-circle mr-2"></i>
                          Details
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </div>

                <TabContent activeTab={activeTab} className="mt-3">
                  <TabPane tabId="history">
                    {renderNotificationList()}
                  </TabPane>
                  <TabPane tabId="details">
                    {renderNotificationDetails()}
                  </TabPane>
                </TabContent>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default NotifyHistory;